<template>
    <div
        :class="[
            'spec-root flex w-full flex-col overflow-hidden bg-gray-50 mx-auto max-w-screen-sm',
            variant === 'page'
                ? 'spec--page h-app max-h-app desk:h-auto desk:max-h-none desk:min-h-0 desk:flex-1 desk:max-w-none desk:overflow-visible'
                : 'spec--sheet h-app max-h-app desk:h-full desk:max-h-full',
        ]"
    >
        <TitleBarCard
            :title="item.name"
            :sharable="true"
            @close="close"
            @share="share"
        />

        <!-- Desktop product page: image + copy | buy column -->
        <div
            v-if="variant === 'page'"
            class="desktop-product-layout flex min-h-0 flex-1 flex-col desk:grid desk:items-start"
        >
            <div class="order-1 min-w-0 desk:border-r desk:border-gray-100" data-view="body" data-spec-image>
                <div class="flex items-start gap-4 border-y border-gray-100 bg-white px-5 py-5 desk:flex-col desk:gap-5 desk:px-8 desk:py-6">
                    <div class="h-36 w-36 shrink-0 overflow-hidden rounded-lg bg-gray-50 desk:h-44 desk:w-44">
                        <img
                            :src="cdnImage(item.image || '/imgs/no_photo.jpg', 240)"
                            :alt="item.name"
                            width="144"
                            height="144"
                            class="h-full w-full object-contain mix-blend-multiply"
                            @error="applyImageFallback($event, item.image)"
                        />
                    </div>
                    <div class="min-w-0 grow">
                        <h1 class="text-lg font-bold leading-snug text-gray-800 desk:text-2xl desk:text-gray-900">{{ item.name }}</h1>
                        <p v-if="selected_product" class="mt-2 text-[13px] text-gray-500">
                            {{ selected_product.name }}
                        </p>
                    </div>
                </div>
                <div
                    v-if="item.description"
                    data-spec-desc
                    class="mt-2 border-y border-gray-100 bg-white px-5 py-6 desk:mt-0 desk:px-8 desk:py-6"
                >
                    <h2 class="mb-3 text-[13px] font-bold text-gray-800">商品介紹</h2>
                    <div class="content-reader max-w-[65ch] overflow-hidden break-words text-[13px] leading-relaxed text-gray-600 desk:text-sm">
                        <HtmlReader :text="item.description" />
                    </div>
                </div>
                <div data-spec-reviews class="mt-2 border-y border-gray-100 bg-white">
                    <ReviewList :category-id="item.id" />
                </div>
            </div>

            <aside
                class="desktop-buy-panel order-2 min-w-0 border-y border-gray-100 bg-white desk:flex desk:flex-col desk:border-y-0"
            >
                <div class="desk:min-h-0 desk:flex-1 desk:overflow-y-auto">
                <SpecificationSkuBlock
                    :item="item"
                    :form="form"
                    :selected-product="selected_product"
                    :stockout="stockout"
                    @select="setForm"
                >
                    <template #options>
                        <GameIdPicker
                            v-if="$auth.loggedIn"
                            :category-id="item.id"
                            @apply="applyGameId"
                        />
                        <div
                            v-for="option in selected_product.fields"
                            :key="option.name"
                            class="mb-5 last:mb-0"
                        >
                            <label
                                class="mb-2 block text-[13px] font-bold text-gray-700"
                                :for="option.name"
                                v-text="option.label_zh != '' ? option.label_zh : option.label"
                            />
                            <OptionInput v-model="form.options[option.name]" :option="option" />
                        </div>
                    </template>
                </SpecificationSkuBlock>
                </div>
                <div
                    data-spec-cta
                    class="spec-cta border-t border-gray-100 bg-white px-5 pt-3 desk:flex-none"
                    :style="{ paddingBottom: 'max(18px, env(safe-area-inset-bottom, 18px))' }"
                >
                    <template v-if="selected_product && !stockout">
                        <div class="mb-3 flex items-center justify-between">
                            <div class="flex items-baseline space-x-1">
                                <span class="text-[12px] font-medium text-gray-500">總計</span>
                                <div class="flex items-baseline text-primary">
                                    <span class="text-sm font-bold">{{ currency }}</span>
                                    <span class="ml-0.5 text-2xl font-black tracking-tight desk:hidden">{{
                                        selected_product.discount || selected_product.price | number_format
                                    }}</span>
                                    <span class="ml-0.5 hidden text-2xl font-black tracking-tight desk:inline">{{ total | number_format }}</span>
                                </div>
                            </div>
                            <QuantityPanel
                                v-model="form.quantity"
                                :disabled="submitting"
                                :onlyonce="forOnce"
                            />
                        </div>
                        <div class="pb-3">
                            <button
                                type="button"
                                class="btn flex h-[52px] w-full items-center justify-center rounded-xl font-bold desk:h-12 desk:rounded-lg"
                                :disabled="submitting"
                                @click="submit"
                            >
                                {{ submitting ? '加入中...' : '加入購物車' }}
                            </button>
                        </div>
                    </template>
                    <RestockRequestPanel
                        v-else
                        :product="selected_product"
                        :category-id="item.id"
                        :category-name="item.name"
                    />
                </div>
            </aside>
        </div>

        <!-- Phone / popup sheet -->
        <div v-else class="spec-body flex min-h-0 flex-1 flex-col">
            <div
                data-desktop-product-split
                class="spec-split grid min-h-0 flex-1 grid-cols-1 overflow-y-auto"
            >
                <main class="min-w-0" data-view="body" data-spec-image>
                    <div class="flex items-start space-x-4 border-b border-gray-100 bg-white px-5 py-5">
                        <div class="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg">
                            <img
                                :src="cdnImage(item.image || '/imgs/no_photo.jpg', 160)"
                                :alt="item.name"
                                width="80"
                                height="80"
                                class="h-full w-full object-contain mix-blend-multiply"
                                @error="applyImageFallback($event, item.image)"
                            />
                        </div>
                        <div class="grow pt-0.5">
                            <h1 class="mb-1 font-bold leading-snug text-gray-800">{{ item.name }}</h1>
                            <p v-if="selected_product" class="line-clamp-2 text-[13px] text-gray-500">
                                {{ selected_product.name }}
                            </p>
                        </div>
                    </div>
                </main>
                <SpecificationSkuBlock
                    :item="item"
                    :form="form"
                    :selected-product="selected_product"
                    :stockout="stockout"
                    @select="setForm"
                >
                    <template #options>
                        <GameIdPicker
                            v-if="$auth.loggedIn"
                            :category-id="item.id"
                            @apply="applyGameId"
                        />
                        <div
                            v-for="option in selected_product.fields"
                            :key="option.name"
                            class="mb-5 last:mb-0"
                        >
                            <label
                                class="mb-2 block text-[13px] font-bold text-gray-700"
                                :for="option.name"
                                v-text="option.label_zh != '' ? option.label_zh : option.label"
                            />
                            <OptionInput v-model="form.options[option.name]" :option="option" />
                        </div>
                    </template>
                </SpecificationSkuBlock>
                <div v-if="item.description" data-spec-desc class="mt-2 bg-white px-5 py-6">
                    <h2 class="mb-3 text-[13px] font-bold text-gray-800">商品介紹</h2>
                    <div class="content-reader overflow-hidden break-words text-[13px] leading-relaxed text-gray-600">
                        <HtmlReader :text="item.description" />
                    </div>
                </div>
                <div data-spec-reviews class="bg-white">
                    <ReviewList :category-id="item.id" />
                </div>
            </div>
            <div
                data-spec-cta
                class="spec-cta z-50 flex-none border-t border-gray-100 bg-white px-5 pt-3"
                :style="{ paddingBottom: 'max(18px, env(safe-area-inset-bottom, 18px))' }"
            >
                <template v-if="selected_product && !stockout">
                    <div class="mb-3 flex items-center justify-between">
                        <div class="flex items-baseline space-x-1">
                            <span class="text-[12px] font-medium text-gray-500">總計</span>
                            <div class="flex items-baseline text-primary">
                                <span class="text-sm font-bold">{{ currency }}</span>
                                <span class="ml-0.5 text-2xl font-black tracking-tight">{{
                                    selected_product.discount || selected_product.price | number_format
                                }}</span>
                            </div>
                        </div>
                        <QuantityPanel
                            v-model="form.quantity"
                            :disabled="submitting"
                            :onlyonce="forOnce"
                        />
                    </div>
                    <div class="pb-3">
                        <button
                            type="button"
                            class="btn flex h-[52px] w-full items-center justify-center rounded-xl font-bold"
                            :disabled="submitting"
                            @click="submit"
                        >
                            加入購物車
                        </button>
                    </div>
                </template>
                <RestockRequestPanel
                    v-else
                    :product="selected_product"
                    :category-id="item.id"
                    :category-name="item.name"
                />
            </div>
        </div>
    </div>
</template>

<script>
import HtmlReader from "@luminexs/components/form/HTMLReader.vue";
import SpecificationItem from "@/components/order/SpecificationItem";
import QuantityPanel from "@/components/order/QuantityPanel";
import OptionInput from "@/components/product/specification/OptionInput";
import GameIdPicker from "@/components/product/GameIdPicker.vue";
import TitleBarCard from "@/components/card/TitleBarCard.vue";
import ReviewList from "@/components/review/ReviewList.vue";
import RestockRequestPanel from "@/components/product/RestockRequestPanel.vue";
import SpecificationSkuBlock from "@/components/product/SpecificationSkuBlock.vue";
import { mapActions, mapGetters } from "vuex";
import { first } from "lodash";
import { applyImageFallback } from "@/utils/desktopImages";

export default {
    components: {
        SpecificationItem,
        QuantityPanel,
        OptionInput,
        GameIdPicker,
        HtmlReader,
        TitleBarCard,
        ReviewList,
        RestockRequestPanel,
        SpecificationSkuBlock,
    },

    props: {
        variant: {
            type: String,
            default: "sheet",
        },
        height: {
            type: String,
            default: "h-app",
        },
        item: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            form: {},
            selected_product: null,
            external: 0,
            submitting: false,
            hasTrackedViewItem: false,
            sharedata: {
                title: "",
                text: "",
                url: "",
            },
        };
    },
    methods: {
        ...mapActions({
            fetch: "cart/fetch",
        }),
        applyImageFallback,

        close() {
            this.$parent.close();
        },

        async share() {
            this.$share.open({
                title: `${this.info.name} - ${this.item.name}`,
                text: this.item.description,
                url: `${window.location.origin}${this.localePath(
                    `/order/product/${this.item.no}`
                )}`,
            });
        },

        setForm(_product) {
            const product =
                _product ||
                first(this.item.products.filter((p) => !p.stockout)) ||
                first(this.item.products);

            if (!product) return;

            this.selected_product = product;

            let form_options = {};
            (product.fields || []).forEach((option) => {
                form_options[option.name] = "";
            });

            this.form = {
                product: product.no,
                options: form_options,
                quantity: 1,
            };
            if (!_product) return;
            this.$nextTick(() => {
                const el = this.$el.querySelector('[data-view="options"]');
                const body = this.$el.querySelector('[data-view="body"]');
                if (el && body && typeof body.scrollTo === "function") {
                    body.scrollTo({
                        top: el.offsetTop - 250,
                        behavior: "smooth",
                    });
                }
            });
        },

        async submit() {
            if (this.submitting || this.stockout) return;
            if (
                this.isGuest(this.localePath(`/order/product/${this.item.no}`))
            ) {
                // Product page close() navigates away and would cancel the login redirect.
                if (this.variant !== "page") {
                    this.close();
                }
                return;
            }
            try {
                this.submitting = true;
                await this.$axios.post("/api/cart", this.form);
                if (this.$track) {
                    this.$track.addToCart(
                        this.item,
                        this.selected_product,
                        this.form.quantity
                    );
                }
                this.fetch();
                this.close();
                this.$loader.success("已加入購物車");
            } catch (e) {
                this.handleErrorsMessage(e);
            } finally {
                this.submitting = false;
            }
        },
        applyGameId(options) {
            if (!this.selected_product || !options) return;

            const nextOptions = { ...this.form.options };

            this.selected_product.fields.forEach((field) => {
                if (Object.prototype.hasOwnProperty.call(options, field.name)) {
                    nextOptions[field.name] = options[field.name];
                }
            });

            this.$set(this.form, "options", nextOptions);
            this.$loader.success("已套用遊戲ID");
        },
        trackViewItem() {
            if (!this.$track || this.hasTrackedViewItem || !this.selected_product) return;

            this.hasTrackedViewItem = true;
            this.$track.viewItem(this.item, this.selected_product);
        },
    },

    computed: {
        ...mapGetters({
            info: "app/info",
            isSafari: "app/isSafari",
        }),
        show() {
            return this.$parent.show;
        },
        price() {
            const { discount, price } = this.selected_product;
            return discount || price;
        },
        total() {
            return this.price * this.form.quantity;
        },
        stockout() {
            if (!this.selected_product) return true;
            return this.selected_product.stockout;
        },
        forOnce() {
            if (!this.selected_product) return true;
            return this.selected_product.once_for_each_only;
        },
    },

    created() {
        this.setForm();
    },
    mounted() {
        this.trackViewItem();
    },
};
</script>
